<template>
	<div class="product-grid-item">
		<div
		  class="product-image"
			@mouseenter="$screen.large_up ? showVariants = true : showVariants = false"
			@mouseleave="showVariants = false">
			<a :href="url">
				<!-- Images -->
				<img
					v-if="media.type === 'image'"
					:src="image"
					:srcset="imageSrcset"
					:sizes="imageSizes"
					:alt="title" />
				<img
					v-if="hoverMedia.type === 'image' && hoverImage"
					:src="hoverImage"
					:srcset="hoverImageSrcset"
					:sizes="hoverImageSizes"
					:alt="title"
					class="hover-image"
					loading="lazy" />

				<!-- Variants -->
				<div v-if="showVariants" class="product-grid-item__variants">
					<div class="product-grid-item__variants-options">
						<div
							class="product-grid-item__variants-options-item"
							:class="{
								'active': selectedVariant === variant,
								'sold-out': variant.available === false,
							}"
							v-for="(variant, index) in product.variants"
							:key="index"
							v-text="variant.title.split(' / ').pop()"
							@click.prevent="selectVariant(variant)">
						</div>
					</div>
					<div class="product-grid-item__variants-atc">
						<button
							v-text="selectedVariant ? 'add to bag' : 'select size'"
							@click.prevent="addToCart()"></button>
					</div>
				</div>

				<div class="product-grid-item__quickbuy">
					<button
						class="product-grid-item__quickbuy-btn"
						@click.prevent="showMobileVariants()"></button>
				</div>

				<!-- Video -->
				<VideoWrapper
					v-if="media.type === 'video' || hoverMedia.type === 'video'"
					:class="{ 'hover-video': hoverMedia.type === 'video' }"
					:media="media.type === 'video' ? media : hoverMedia" />

				<!-- Labels -->
				<div v-if="labels.length" class="labels">
					<div v-for="(label, index) in labels" :key="index" class="label">
						{{ label }}
					</div>
				</div>
			</a>

			<a
				v-if="styling !== null"
				href="#"
				class="similar-styles"
				@click.prevent="toggleFilter"
				v-text="$language.product.similar_styles"></a>
		</div>

		<div :class="['product-text', { 'has-swatches': swatches.length }]">
			<div class="product-text-left">
				<a :href="url" class="link">
					<p class="title">{{ title }}</p>
					<p class="colour">{{ colour }}</p>

					<p v-if="product.vendor" class="vendor">{{ product.vendor }}</p>

					<p v-if="product.compare_at_price_min > product.price_min" class="price sale">
						<span itemprop="price">{{ moneyFormat(product, 'price_min') }}</span>
						<s>{{ moneyFormat(product, 'compare_at_price_min') }}</s>
					</p>
					<p v-else class="price">
						<span itemprop="price">{{ moneyFormat(product, 'price_min') }}</span>
					</p>
				</a>
			</div>
			<div
				v-if="swatches.length"
				class="product-grid-swatches">
				<ul>
					<li v-for="(swatch, index) in swatches" :key="index">
						<a :href="swatch.url" @click.prevent="updateProduct(swatch.product)" :title="swatch.title">
							<div :class="['swatch', { 'active': swatch.product.handle == product.handle }]"
								:style="{ 'background-color': swatch.colour }">
							</div>
							<span class="visually-hidden">{{ swatch.title }}</span>
						</a>
					</li>
				</ul>
			</div>
		</div>

		<WishlistButton :pid="product.id" :vid="product.variants[0].id" />

		<div
			v-if="showVariantsMobile"
			class="product-item__mobile-variants">
			<div class="product-item__mobile-variants-options">
				<div
					class="product-item__mobile-variants-options-item"
					:class="{
						'active': selectedVariant === variant,
						'sold-out': variant.available === false,
					}"
					v-for="(variant, index) in product.variants"
					:key="index"
					v-text="variant.title.split(' / ').pop()"
					@click.prevent="selectVariant(variant)">
				</div>
			</div>
			<div class="product-item__mobile-variants-atc">
				<button
					v-text="selectedVariant ? 'add to bag' : 'select size'"
					@click.prevent="addToCart()"></button>
			</div>
		</div>
	</div>
</template>

<script>
import { formatMoney } from '@shopify/theme-currency'
import { getSizedImageUrl } from '@shopify/theme-images'
import { addItem } from '../../scripts/cart';
import WishlistButton from '../Components/WishlistButton'
import VideoWrapper from '../Components/VideoWrapper'

export default {
	name: 'ProductItem',

	props: [
		'product',
		'collection',
		'similarStyles',
	],

	components: { WishlistButton, VideoWrapper },

	data() {
		return {
			swatches: [],
			showVariants: false,
			showVariantsMobile: false,
			selectedVariant: null,
			video: this.product.media.filter(media => media.mediaContentType === "VIDEO")
		}
	},

	computed: {
		handle() {
			return this.product.handle
		},

		title() {
			return this.product.title.split(' / ')[0]
		},

		colour() {
			return this.product.title.split(' / ')[1]
		},

		url() {
			return typeof this.collection != 'undefined' ? `/collections/${this.collection}/products/${this.handle}` : `/collections/${this.product.collections[0].handle}/products/${this.handle}`
		},

		media() {
			if (this.product.media[0].mediaContentType === "VIDEO" && this.video.length > 0) {
				return {
					'type': 'video',
					'preview_image': this.video[0].preview.image.originalSrc,
					'sources': this.video[0].sources
				};
			}

			return {
				'type': 'image',
				'url': this.image
			};
		},

		hoverMedia() {
			if (this.product.media[0].mediaContentType !== "VIDEO" && this.video.length > 0) {
				return {
					'type': 'video',
					'preview_image': this.video[0].preview.image.originalSrc,
					'sources': this.video[0].sources
				};
			}

			return {
				'type': 'image',
				'url': this.hoverImage
			};
		},

		image() {
			let imageSrc = this.product.images['2']

			if (this.$parent.imageView == 'outfit') {
				if (this.product.images['1']) {
					imageSrc = this.product.images['1']
				}
			}
			return this.imageFormat(imageSrc, '300x450_crop_center')
		},

		hoverImage() {
			let hoverImageSrc = this.product.images['1']

			if (this.$parent.imageView == 'outfit') {
				// 1. Swap hover image to be position #1 by default and then #3 on hover
				hoverImageSrc = this.product.images['3']
			}

			if (hoverImageSrc) {
				return this.imageFormat(hoverImageSrc, '10x15_crop_center')
			} else {
				return null
			}
		},

		imageSrcset() {
			let imageSrc = this.product.images['2'],
				sizesArray = ['300x450_crop_center', '500x750_crop_center', '750x1125_crop_center', '930x1395_crop_center']

			if (this.$parent.imageView == 'outfit') {
				if (this.product.images['1']) {
					imageSrc = this.product.images['1']
				}
			}

			if (this.$parent.gridSize == 'large') {
				sizesArray = ['300x450_crop_center', '500x750_crop_center', '750x1125_crop_center', '940x1410_crop_center', '1410x2115_crop_center', '1880x2820_crop_center']
			}

			return this.srcsetFormat(imageSrc, sizesArray)
		},

		hoverImageSrcset() {
			let hoverImageSrc = this.product.images['1'],
				sizesArray = ['10x15_crop_center', '300x450_crop_center', '500x750_crop_center', '750x1125_crop_center', '930x1395_crop_center']

			if (this.$parent.imageView == 'outfit') {
				// 1. Swap hover image to be position #1 by default and then #3 on hover
				hoverImageSrc = this.product.images['3']
			}

			if (this.$parent.gridSize == 'large') {
				sizesArray = ['10x15_crop_center', '300x450_crop_center', '500x750_crop_center', '750x1125_crop_center', '940x1410_crop_center', '1410x2115_crop_center', '1880x2820_crop_center']
			}

			if (hoverImageSrc) {
				return this.srcsetFormat(hoverImageSrc, sizesArray)
			} else {
				return null
			}
		},

		imageSizes() {
			let imageSizes = '(min-width: 2000px) 465px, (min-width: 990px) calc(25vw - 35px), (min-width: 870px) calc(25vw - 17.5px), calc(50vw - 25px)'

			if (this.$parent.gridSize == 'large') {
				imageSizes = '(min-width: 2000px) 940px, (min-width: 990px) calc(50vw - 60px), (min-width: 870px) calc(50vw - 25px), calc(100vw - 40px)'
			}

			return imageSizes
		},

		hoverImageSizes() {
			let hoverImageSizes = '(min-width: 2000px) 465px, (min-width: 990px) calc(25vw - 35px), 2px'

			if (this.$parent.gridSize == 'large') {
				hoverImageSizes = '(min-width: 2000px) 940px, (min-width: 990px) calc(50vw - 60px), 2px'
			}

			return hoverImageSizes
		},

		labels() {
			let labels = [],
				labelCount = 0;

			if (this.product.available) {
				// General labels
				this.product.tags.forEach(function (tag) {
					if (tag.includes('label collection - ')) {
						if (labelCount < 2) {
							labels.push(tag.replace('label collection - ', ''))
							labelCount++
						}
					}
				})

				// Sale label
				if (this.product.product_type != 'bundle' && this.product.compare_at_price_min > this.product.price_min) {
					if (labelCount < 2) {
						labels.push(this.$language.product.on_sale)
					}
				}

				// Sold out label
			} else {
				labels.push(this.$language.product.sold_out)
			}

			// Selling fast
			let qos = 0
			this.product.variants.forEach((v) => qos += (v?.inventory_quantity && v.inventory_quantity >= 0) ? v.inventory_quantity : 0)
			if (11 <= qos && qos <= 30) labels.push('selling fast')

			return labels
		},
		styling: function () {
			const vendor_mapping = { "m o o c h i": 'moochi', "six & seven": '6&7', "bread & butter": 'bread&butter' };
			const vendor_mapping2 = { 'moochi': "m o o c h i", '6&7': "six & seven", 'bread&butter': "bread & butter" };
			let vendor, color;
			let val = null
			if (!val) return val
			vendor = this.product['vendor']
			if (Object.keys(vendor_mapping).indexOf(vendor) !== -1) vendor = vendor_mapping[vendor]
			val = val[vendor] || null
			if (!val) return val
			color = this.product.variants[0].merged_options[0].replace('colour:', '').trim()
			if (color.indexOf('|') !== -1) color = color.split('|')[0].trim()
			val = val[color] || null
			if (!val) return val
			// format the filter params
			val['params'] = val['params'].map(v => {
				if (v['key'] == 'pf_v_label') v['value'] = vendor_mapping2[v['value']] || v['value']
				v['value'] = v['value'].trim()
				return v
			})
			return val
		},
	},

	mounted() {
		this.loadSwatches();

		// Remove mobile variants selector on overlay click
		document.querySelector('.overlay').addEventListener('click', () => {
			this.showVariantsMobile = false;
			document.querySelector('.overlay').classList.remove('shown');
		})
	},

	methods: {
		async loadSwatches() {
			if (this.product.handle) {
				fetch('/products/' + this.product.handle + '?view=swatches')
					.then(res => res.json())
					.then(data => {
						if (data.swatches.length > 0) {
							this.swatches = data.swatches
						}
					})
					.catch(error => {
						console.error(error)
					})
			}
		},

		showMobileVariants() {
			this.showVariantsMobile = true;
			document.querySelector('.overlay').classList.add('shown');
		},

		selectVariant(variant) {
			if (variant.available) {
				this.selectedVariant = variant;
			}
		},

		addToCart() {
      const variantID = Number(this.selectedVariant.id);

			let properties = {};

			if (Number(this.selectedVariant.compare_at_price) > Number(this.selectedVariant.price)) {
				properties = {
					'_is_sale': "true"
				}
			}

      addItem(variantID, {
        quantity: 1,
				properties: properties
      })
        .then(
          (cart) => {
            console.log(cart)
          },
          (failure) => {
            Promise.resolve(failure).then((data) => {
              alert(data.description);
            });
          }
        )
        .finally(() => {
          document.querySelector('a.toggle-mini-cart').click();
					this.showVariantsMobile = false;
					this.selectedVariant = null;
        });
		},

		moneyFormat(product, type) {
			let currency_type = type + '_' + theme.currency.toLowerCase()

			if (!(currency_type in product)) {
				currency_type = type
			}

			return formatMoney(product[currency_type] * 100, theme.moneyFormat)
		},

		imageFormat(src, size) {
			if (!src) {
				src = theme.defaultImage
			}

			return getSizedImageUrl(src, size)
		},

		srcsetFormat(src, sizes) {
			let output = ''

			if (!src) {
				src = theme.defaultImage
			}

			sizes.forEach(function (size, i) {
				if (i !== 0) {
					output += ', '
				}

				// Get first number from Shopify image size (split at x)
				let srcsetWidth = size.split('x', 1)[0]

				output += getSizedImageUrl(src, size) + ' ' + srcsetWidth + 'w'
			})

			return output
		},

		updateProduct(product) {
			let currency_type = theme.currency.toLowerCase()

			this.product.available = product.available
			this.product.collections = product.collections
			this.product.compare_at_price_min = product.compare_at_price_min
			this.product['compare_at_price_min_' + currency_type] = product['compare_at_price_min_' + currency_type]
			this.product.handle = product.handle
			this.product.images = product.images
			this.product.price_min = product.price_min
			this.product['price_min_' + currency_type] = product['price_min_' + currency_type]
			this.product.tags = product.tags
			this.product.title = product.title
		},

		toggleFilter: function () {
			this.$emit('styling', this.styling)
		},
	}
}
</script>
